// 问卷步骤3：绿色项目与发行计划
// 包含绿色项目情况、发行规模、时间安排等

import React from 'react';
import {
  Form,
  Select,
  Radio,
  Checkbox,
  Input,
  Space,
  Typography,
  Row,
  Col,
  Card,
  Alert,
  Tag,
} from 'antd';
import {
  BranchesOutlined,
  RocketOutlined,
  CalendarOutlined,
  GlobalOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  GreenProjectStatus,
  GreenProjectCategory,
  GreenStandardCompliance,
  InvestmentScale,
  IssuanceScale,
  IssuanceTime,
  ProductType,
  TargetMarket,
  InitialScreeningQuestionnaire,
} from '../../../types/questionnaire';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface QuestionnaireStep3Props {
  form: any;
  questionnaire: Partial<InitialScreeningQuestionnaire>;
  onUpdate: (data: Partial<InitialScreeningQuestionnaire>) => void;
}

// 选项标签映射 - 使用翻译函数
const getGreenProjectStatusLabels = (t: (key: string) => string) => ({
  [GreenProjectStatus.CLEAR_PROJECT]: t(
    'questionnaire:options.greenProjectStatus.clear_project'
  ),
  [GreenProjectStatus.IN_PLANNING]: t(
    'questionnaire:options.greenProjectStatus.in_planning'
  ),
  [GreenProjectStatus.NO_CLEAR_PROJECT]: t(
    'questionnaire:options.greenProjectStatus.no_clear_project'
  ),
});

const getGreenProjectCategoryLabels = (t: (key: string) => string) => ({
  [GreenProjectCategory.RENEWABLE_ENERGY]: t(
    'questionnaire:options.greenProjectCategory.renewable_energy'
  ),
  [GreenProjectCategory.ENERGY_SAVING]: t(
    'questionnaire:options.greenProjectCategory.energy_saving'
  ),
  [GreenProjectCategory.ENVIRONMENTAL_TREATMENT]: t(
    'questionnaire:options.greenProjectCategory.environmental_treatment'
  ),
  [GreenProjectCategory.CLEAN_TRANSPORTATION]: t(
    'questionnaire:options.greenProjectCategory.clean_transportation'
  ),
  [GreenProjectCategory.GREEN_BUILDING]: t(
    'questionnaire:options.greenProjectCategory.green_building'
  ),
  [GreenProjectCategory.CIRCULAR_ECONOMY]: t(
    'questionnaire:options.greenProjectCategory.circular_economy'
  ),
  [GreenProjectCategory.ECOLOGICAL_PROTECTION]: t(
    'questionnaire:options.greenProjectCategory.ecological_protection'
  ),
  [GreenProjectCategory.OTHER_GREEN]: t(
    'questionnaire:options.greenProjectCategory.other_green'
  ),
});

const getGreenStandardComplianceLabels = (t: (key: string) => string) => ({
  [GreenStandardCompliance.CERTIFIED]: t(
    'questionnaire:options.greenStandardCompliance.certified'
  ),
  [GreenStandardCompliance.PBOC_COMPLIANT]: t(
    'questionnaire:options.greenStandardCompliance.pboc_compliant'
  ),
  [GreenStandardCompliance.INTERNATIONAL_COMPLIANT]: t(
    'questionnaire:options.greenStandardCompliance.international_compliant'
  ),
  [GreenStandardCompliance.BASICALLY_COMPLIANT]: t(
    'questionnaire:options.greenStandardCompliance.basically_compliant'
  ),
  [GreenStandardCompliance.UNCERTAIN]: t(
    'questionnaire:options.greenStandardCompliance.uncertain'
  ),
});

const getInvestmentScaleLabels = (t: (key: string) => string) => ({
  [InvestmentScale.ABOVE_10B]: t(
    'questionnaire:options.investmentScale.above_10b'
  ),
  [InvestmentScale.FROM_5B_TO_10B]: t(
    'questionnaire:options.investmentScale.from_5b_to_10b'
  ),
  [InvestmentScale.FROM_2B_TO_5B]: t(
    'questionnaire:options.investmentScale.from_2b_to_5b'
  ),
  [InvestmentScale.FROM_1B_TO_2B]: t(
    'questionnaire:options.investmentScale.from_1b_to_2b'
  ),
  [InvestmentScale.FROM_500M_TO_1B]: t(
    'questionnaire:options.investmentScale.from_500m_to_1b'
  ),
  [InvestmentScale.BELOW_500M]: t(
    'questionnaire:options.investmentScale.below_500m'
  ),
});

const getIssuanceScaleLabels = (t: (key: string) => string) => ({
  [IssuanceScale.ABOVE_5B]: t('questionnaire:options.issuanceScale.above_5b'),
  [IssuanceScale.FROM_2B_TO_5B]: t(
    'questionnaire:options.issuanceScale.from_2b_to_5b'
  ),
  [IssuanceScale.FROM_1B_TO_2B]: t(
    'questionnaire:options.issuanceScale.from_1b_to_2b'
  ),
  [IssuanceScale.FROM_500M_TO_1B]: t(
    'questionnaire:options.issuanceScale.from_500m_to_1b'
  ),
  [IssuanceScale.FROM_300M_TO_500M]: t(
    'questionnaire:options.issuanceScale.from_300m_to_500m'
  ),
  [IssuanceScale.BELOW_300M]: t(
    'questionnaire:options.issuanceScale.below_300m'
  ),
});

const getIssuanceTimeLabels = (t: (key: string) => string) => ({
  [IssuanceTime.WITHIN_1_MONTH]: t(
    'questionnaire:options.issuanceTime.within_1_month'
  ),
  [IssuanceTime.WITHIN_3_MONTHS]: t(
    'questionnaire:options.issuanceTime.within_3_months'
  ),
  [IssuanceTime.WITHIN_6_MONTHS]: t(
    'questionnaire:options.issuanceTime.within_6_months'
  ),
  [IssuanceTime.WITHIN_1_YEAR]: t(
    'questionnaire:options.issuanceTime.within_1_year'
  ),
  [IssuanceTime.ABOVE_1_YEAR]: t(
    'questionnaire:options.issuanceTime.above_1_year'
  ),
});

const getProductTypeLabels = (t: (key: string) => string) => ({
  [ProductType.PUBLIC_GREEN_BOND]: t(
    'questionnaire:options.productType.public_green_bond'
  ),
  [ProductType.PRIVATE_GREEN_BOND]: t(
    'questionnaire:options.productType.private_green_bond'
  ),
  [ProductType.GREEN_MTN]: t('questionnaire:options.productType.green_mtn'),
  [ProductType.GREEN_SYNDICATED_LOAN]: t(
    'questionnaire:options.productType.green_syndicated_loan'
  ),
  [ProductType.GREEN_ABS_REITS]: t(
    'questionnaire:options.productType.green_abs_reits'
  ),
  [ProductType.OTHER_GREEN_PRODUCT]: t(
    'questionnaire:options.productType.other_green_product'
  ),
});

const getTargetMarketLabels = (t: (key: string) => string) => ({
  [TargetMarket.DOMESTIC]: t('questionnaire:options.targetMarket.domestic'),
  [TargetMarket.OFFSHORE]: t('questionnaire:options.targetMarket.offshore'),
  [TargetMarket.MACAU]: t('questionnaire:options.targetMarket.macau'),
  [TargetMarket.FLEXIBLE]: t('questionnaire:options.targetMarket.flexible'),
});

export const QuestionnaireStep3: React.FC<QuestionnaireStep3Props> = ({
  form,
  questionnaire,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const watchedValues = Form.useWatch([], form) as any;
  const hasGreenProject = watchedValues?.greenProjectInfo?.hasGreenProject;
  const showProjectDetails =
    hasGreenProject && hasGreenProject !== GreenProjectStatus.NO_CLEAR_PROJECT;

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      {/* 绿色项目说明 */}
      <Alert
        message={t('questionnaire:step3.greenProjectDesc')}
        description={t('questionnaire:step3.greenProjectDescContent')}
        type="info"
        showIcon
        icon={<BranchesOutlined />}
        style={{
          borderRadius: '8px',
          borderColor: '#10b981',
          backgroundColor: 'rgba(16, 185, 129, 0.05)',
        }}
      />

      {/* C1. 绿色项目基本情况 */}
      <Card
        title={
          <div
            style={{
              background:
                'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
              margin: '-12px -16px 12px -16px',
              padding: '12px 16px',
              borderRadius: '12px 12px 0 0',
              borderBottom: '1px solid rgba(16, 185, 129, 0.1)',
            }}
          >
            <Space>
              <EnvironmentOutlined style={{ color: '#10b981' }} />
              <Text strong>{t('questionnaire:step3.greenProjectSection')}</Text>
            </Space>
          </div>
        }
        style={{
          borderRadius: '16px',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(16, 185, 129, 0.1)',
          boxShadow: '0 8px 32px rgba(16, 185, 129, 0.08)',
          transition: 'all 0.3s ease',
        }}
        styles={{ body: { padding: '24px' } }}
      >
        <Row gutter={[16, 24]}>
          <Col xs={24}>
            <Form.Item
              name={['greenProjectInfo', 'hasGreenProject']}
              label={t('questionnaire:step3.hasGreenProject')}
              rules={[
                {
                  required: true,
                  message: t('questionnaire:step3.hasGreenProjectRequired'),
                },
              ]}
              extra={t('questionnaire:step3.hasGreenProjectExtra')}
            >
              <Radio.Group style={{ width: '100%' }}>
                <Row gutter={[16, 12]}>
                  {Object.entries(getGreenProjectStatusLabels(t)).map(
                    ([value, label]) => (
                      <Col xs={24} sm={12} key={value}>
                        <Radio
                          value={value}
                          style={{ width: '100%', padding: '8px' }}
                        >
                          {label}
                        </Radio>
                      </Col>
                    )
                  )}
                </Row>
              </Radio.Group>
            </Form.Item>
          </Col>

          {/* 项目详情（条件显示） */}
          {showProjectDetails && (
            <>
              {/* 项目名称 - 从Step1移动到这里 */}
              <Col xs={24}>
                <Form.Item
                  name="projectName"
                  label={t('questionnaire:step3.projectName')}
                  rules={[
                    {
                      required: true,
                      message: t('questionnaire:step3.projectNameRequired'),
                    },
                  ]}
                  extra={t('questionnaire:step3.projectNameExtra')}
                >
                  <Input
                    placeholder={t(
                      'questionnaire:step3.projectNamePlaceholder'
                    )}
                    prefix={<RocketOutlined style={{ color: '#10b981' }} />}
                    size="large"
                    style={{ borderRadius: '8px' }}
                  />
                </Form.Item>
              </Col>

              {/* 项目所在地 */}
              <Col xs={24}>
                <Form.Item
                  name={['greenProjectInfo', 'projectLocation']}
                  label={t('questionnaire:step3.projectLocation')}
                  rules={[
                    {
                      required: true,
                      message: t('questionnaire:step3.projectLocationRequired'),
                    },
                  ]}
                >
                  <Radio.Group>
                    <Radio value="mainland_china">
                      {t('questionnaire:step3.mainlandChina')}
                    </Radio>
                    <Radio value="overseas">
                      {t('questionnaire:step3.overseas')}
                    </Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  name={['greenProjectInfo', 'projectCategories']}
                  label={t('questionnaire:step3.projectCategories')}
                  rules={[
                    {
                      required: true,
                      message: t(
                        'questionnaire:step3.projectCategoriesRequired'
                      ),
                    },
                  ]}
                  extra={t('questionnaire:step3.projectCategoriesExtra')}
                >
                  <Checkbox.Group style={{ width: '100%' }}>
                    <Row
                      gutter={[16, 12]}
                      className="questionnaire-project-category-grid"
                    >
                      {Object.entries(getGreenProjectCategoryLabels(t)).map(
                        ([value, label]) => (
                          <Col xs={24} sm={12} md={8} key={value}>
                            <Checkbox
                              value={value}
                              className="questionnaire-project-category-checkbox"
                            >
                              <Tag
                                color="green"
                                style={{ margin: 0 }}
                                className="questionnaire-project-category-tag"
                              >
                                {label}
                              </Tag>
                            </Checkbox>
                          </Col>
                        )
                      )}
                    </Row>
                  </Checkbox.Group>
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  name={['greenProjectInfo', 'projectInvestmentScale']}
                  label={t('questionnaire:step3.investmentScale')}
                  rules={[
                    {
                      required: true,
                      message: t('questionnaire:step3.investmentScaleRequired'),
                    },
                  ]}
                  extra={t('questionnaire:step3.investmentScaleExtra')}
                >
                  <Select
                    placeholder={t(
                      'questionnaire:step3.investmentScalePlaceholder'
                    )}
                  >
                    {Object.entries(getInvestmentScaleLabels(t)).map(
                      ([value, label]) => (
                        <Option key={value} value={value}>
                          {label}
                        </Option>
                      )
                    )}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  name={['greenProjectInfo', 'greenStandardCompliance']}
                  label={t('questionnaire:step3.greenStandardCompliance')}
                  rules={[
                    {
                      required: true,
                      message: t(
                        'questionnaire:step3.greenStandardComplianceRequired'
                      ),
                    },
                  ]}
                  extra={t('questionnaire:step3.greenStandardComplianceExtra')}
                >
                  <Select
                    placeholder={t(
                      'questionnaire:step3.greenStandardCompliancePlaceholder'
                    )}
                  >
                    {Object.entries(getGreenStandardComplianceLabels(t)).map(
                      ([value, label]) => (
                        <Option key={value} value={value}>
                          {label}
                        </Option>
                      )
                    )}
                  </Select>
                </Form.Item>
              </Col>
            </>
          )}
        </Row>
      </Card>

      {/* C2. 发行计划 */}
      <Card
        title={
          <div
            style={{
              background:
                'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
              margin: '-12px -16px 12px -16px',
              padding: '12px 16px',
              borderRadius: '12px 12px 0 0',
              borderBottom: '1px solid rgba(16, 185, 129, 0.1)',
            }}
          >
            <Space>
              <RocketOutlined style={{ color: '#10b981' }} />
              <Text strong>{t('questionnaire:step3.issuancePlanSection')}</Text>
            </Space>
          </div>
        }
        style={{
          borderRadius: '16px',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(16, 185, 129, 0.1)',
          boxShadow: '0 8px 32px rgba(16, 185, 129, 0.08)',
          transition: 'all 0.3s ease',
        }}
        styles={{ body: { padding: '24px' } }}
      >
        <Row gutter={[16, 24]}>
          <Col xs={24} md={12}>
            <Form.Item
              name={['issuancePlan', 'plannedIssuanceScale']}
              label={t('questionnaire:step3.plannedIssuanceScale')}
              rules={[
                {
                  required: true,
                  message: t(
                    'questionnaire:step3.plannedIssuanceScaleRequired'
                  ),
                },
              ]}
              extra={t('questionnaire:step3.plannedIssuanceScaleExtra')}
            >
              <Select
                placeholder={t(
                  'questionnaire:step3.plannedIssuanceScalePlaceholder'
                )}
              >
                {Object.entries(getIssuanceScaleLabels(t)).map(
                  ([value, label]) => (
                    <Option key={value} value={value}>
                      {label}
                    </Option>
                  )
                )}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name={['issuancePlan', 'plannedIssuanceTime']}
              label={t('questionnaire:step3.plannedIssuanceTime')}
              rules={[
                {
                  required: true,
                  message: t('questionnaire:step3.plannedIssuanceTimeRequired'),
                },
              ]}
              extra={t('questionnaire:step3.plannedIssuanceTimeExtra')}
            >
              <Select
                placeholder={t(
                  'questionnaire:step3.plannedIssuanceTimePlaceholder'
                )}
              >
                {Object.entries(getIssuanceTimeLabels(t)).map(
                  ([value, label]) => (
                    <Option key={value} value={value}>
                      {label}
                    </Option>
                  )
                )}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name={['issuancePlan', 'targetMarket']}
              label={t('questionnaire:step3.targetMarket')}
              rules={[
                {
                  required: true,
                  message: t('questionnaire:step3.targetMarketRequired'),
                },
              ]}
              extra={t('questionnaire:step3.targetMarketExtra')}
              initialValue={TargetMarket.MACAU}
            >
              <Select
                placeholder={t('questionnaire:step3.targetMarketPlaceholder')}
                defaultValue={TargetMarket.MACAU}
              >
                {Object.entries(getTargetMarketLabels(t)).map(
                  ([value, label]) => (
                    <Option key={value} value={value}>
                      {label}
                    </Option>
                  )
                )}
              </Select>
            </Form.Item>
          </Col>

          {/* Bug修复: 添加发行产品类型字段 */}
          <Col xs={24} md={12}>
            <Form.Item
              name={['issuancePlan', 'productType']}
              label={t('questionnaire:step3.productType')}
              rules={[
                {
                  required: true,
                  message: t('questionnaire:step3.productTypeRequired'),
                },
              ]}
              extra={t('questionnaire:step3.productTypeExtra')}
            >
              <Select
                placeholder={t('questionnaire:step3.productTypePlaceholder')}
              >
                {Object.entries(getProductTypeLabels(t)).map(
                  ([value, label]) => (
                    <Option key={value} value={value}>
                      {label}
                    </Option>
                  )
                )}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* 项目描述（可选） */}
      {showProjectDetails && (
        <Card
          title={
            <div
              style={{
                background:
                  'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
                margin: '-12px -16px 12px -16px',
                padding: '12px 16px',
                borderRadius: '12px 12px 0 0',
                borderBottom: '1px solid rgba(16, 185, 129, 0.1)',
              }}
            >
              <Space>
                <BranchesOutlined style={{ color: '#10b981' }} />
                <Text strong>
                  {t('questionnaire:step3.projectDescSection')}
                </Text>
              </Space>
            </div>
          }
          style={{
            borderRadius: '16px',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(16, 185, 129, 0.1)',
            boxShadow: '0 8px 32px rgba(16, 185, 129, 0.08)',
            transition: 'all 0.3s ease',
          }}
          styles={{ body: { padding: '24px' } }}
        >
          <Form.Item
            name="projectDescription"
            label={t('questionnaire:step3.projectDescLabel')}
            className="project-description-item"
            style={{ marginBottom: '0' }}
          >
            <TextArea
              rows={8}
              placeholder={t('questionnaire:step3.projectDescPlaceholder')}
              maxLength={500}
              className="project-description-textarea"
              style={{
                width: '100%',
                minHeight: '200px',
                fontSize: '16px',
                lineHeight: '1.6',
                padding: '12px',
                boxSizing: 'border-box',
                display: 'block',
              }}
            />
          </Form.Item>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginTop: '8px',
              marginBottom: '16px',
              gap: '12px',
            }}
          >
            <span
              style={{
                fontSize: '12px',
                color: '#6b7280',
                lineHeight: '1.5',
                flex: 1,
              }}
            >
              {t('questionnaire:step3.projectDescHint')}
            </span>
            <span
              style={{
                fontSize: '12px',
                color: '#999',
                whiteSpace: 'nowrap',
              }}
            >
              {form.getFieldValue('projectDescription')?.length || 0}/500
            </span>
          </div>
        </Card>
      )}

      {/* 绿色项目提示 */}
      <Card
        style={{
          borderRadius: '16px',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(16, 185, 129, 0.1)',
          boxShadow: '0 8px 32px rgba(16, 185, 129, 0.08)',
          transition: 'all 0.3s ease',
        }}
        styles={{ body: { padding: '16px' } }}
      >
        <Space>
          <GlobalOutlined style={{ color: '#10b981' }} />
          <div>
            <Text style={{ color: '#10b981' }}>
              <strong>{t('questionnaire:step3.greenProjectTips')}</strong>
            </Text>
            <div style={{ marginTop: '4px', color: '#6b7280' }}>
              • {t('questionnaire:step3.greenTip1')}
              <br />• {t('questionnaire:step3.greenTip2')}
              <br />• {t('questionnaire:step3.greenTip3')}
              <br />• {t('questionnaire:step3.greenTip4')}
            </div>
          </div>
        </Space>
      </Card>
    </Space>
  );
};
